import React, { FC, MutableRefObject, ReactElement, useContext, useEffect, useRef, useState } from 'react'

import useIsMountedRef from 'use-is-mounted-ref'
import { ValidateResult } from '../../../Vendor/react-hook-form/dist/index.ie11'
import { AgentDetails } from '../../../../i18n'
import { LOADING_STRING } from '../../../NonReact/constants/Common'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { IsStringEmpty, isStringNotEmpty, IsVarEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { ALL_VALUE, getPartitionsForTelServer } from '../../Common/API/ItemsGetters'
import { ComboBox } from '../../Common/Components/ComboBox/ComboBox'
import FormRow from '../../Common/Components/FormRow'
import { SelectOption, SelectOptions } from '../../Common/Components/Selects/Types'
import { ValidationErrors } from '../../Common/Components/ValidationErrors/ValidationErrors'
import { LanguageContext, ReactState } from '../../Common/Contexts'
import MessageContext, { MessageContextType } from '../../Common/Contexts/MessageContext'
import {
    AGENT_NUMBER_FIELD_NAME,
    DESCRIPTION_FIELD_NAME,
    GUID_FIELD_NAME,
    PARTITION_FIELD_NAME,
    UPDATE_ITEMS_FIELD_NAME
} from '../Constants/FieldNames'
import { ConfigureAgentsContext, ConfigureAgentsContextType } from '../Contexts/ConfigureAgentsContext'
import { RouterCheckbox } from '../../../ReactRouting/Components/Atoms/Inputs/RouterCheckbox'
import { OutlinedTextInput } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedTextInput'

export const AgentDetailsTab: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(AgentDetails.NAME)

    const { displayError }: MessageContextType = useContext(MessageContext)
    const {
        serverTypeConfig: { isGUID, isAgentNumber, isDescription, isPartition, isUpdateDuplicateItems }
    }: ConfigureAgentsContextType = useContext(ConfigureAgentsContext)

    const {
        viewOnly,
        selectedAgent,
        filters: { telSysID, partitionID }
    }: ConfigureAgentsContextType = useContext(ConfigureAgentsContext)
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const [isPartitionsLoading, setIsPartitionsLoading]: ReactState<boolean> = useState<boolean>(false)
    const [availablePartitions, setAvailablePartitions]: ReactState<SelectOptions<number>> = useState<
        SelectOptions<number>
    >([
        {
            label: LOADING_STRING,
            value: partitionID
        }
    ])

    const nonePartitionOption: MutableRefObject<SelectOption<number>> = useRef({
        label: language.get(AgentDetails.AgentDetails_NonePartition),
        value: ALL_VALUE
    })

    useEffect((): void => {
        setIsPartitionsLoading(true)
        getPartitionsForTelServer(telSysID)
            .then((partitions: SelectOptions<number>): void => {
                if (isMounted.current) {
                    setAvailablePartitions(partitions)
                    setIsPartitionsLoading(false)
                }
            })
            .catch(displayError)
    }, [])

    return (
        <div className='ca-details-tab tab-area'>
            {isGUID ? (
                <FormRow>
                    <ValidationErrors name={GUID_FIELD_NAME}>
                        <OutlinedTextInput
                            placeholder={language.get(AgentDetails.AgentDetails_GUIDPlaceholder)}
                            labelLeft={language.get(AgentDetails.AgentDetails_GUID)}
                            inputName={GUID_FIELD_NAME}
                            variant='outlined'
                            rules={{
                                maxLength: {
                                    value: 100,
                                    message: language.get(AgentDetails.AgentDetails_Validation_GUID)
                                }
                            }}
                            disabled={viewOnly || isStringNotEmpty(selectedAgent)}
                        />
                    </ValidationErrors>
                </FormRow>
            ) : (
                <></>
            )}
            {isAgentNumber ? (
                <FormRow>
                    <ValidationErrors name={AGENT_NUMBER_FIELD_NAME}>
                        <OutlinedTextInput
                            labelLeft={language.get(AgentDetails.AgentNumber)}
                            inputName={AGENT_NUMBER_FIELD_NAME}
                            variant='outlined'
                            rules={{
                                required: language.get(AgentDetails.AgentDetails_Validation_AgentNumber)
                            }}
                            disabled={viewOnly}
                        />
                    </ValidationErrors>
                </FormRow>
            ) : (
                <></>
            )}
            {isPartition ? (
                <FormRow>
                    <ValidationErrors name={PARTITION_FIELD_NAME}>
                        <ComboBox
                            defaultOption={nonePartitionOption.current}
                            options={availablePartitions}
                            label={language.get(AgentDetails.AgentDetails_Partition)}
                            variant='outlined'
                            name={PARTITION_FIELD_NAME}
                            loading={isPartitionsLoading}
                            noChange={isPartitionsLoading}
                            rules={{
                                validate: {
                                    singleParitionRequired: (value: number): ValidateResult => {
                                        if (value === ALL_VALUE || IsVarEmpty(value)) {
                                            return language.get(AgentDetails.AgentDetails_Validation_Partition)
                                        }

                                        return undefined
                                    }
                                }
                            }}
                            disabled={viewOnly || isStringNotEmpty(selectedAgent)}
                        />
                    </ValidationErrors>
                </FormRow>
            ) : (
                <></>
            )}
            {isDescription ? (
                <FormRow>
                    <ValidationErrors name={DESCRIPTION_FIELD_NAME}>
                        <OutlinedTextInput
                            labelLeft={language.get(AgentDetails.AgentDetails_Description)}
                            inputName={DESCRIPTION_FIELD_NAME}
                            disabled={viewOnly}
                            variant='outlined'
                        />
                    </ValidationErrors>
                </FormRow>
            ) : (
                <></>
            )}
            {isUpdateDuplicateItems && IsStringEmpty(selectedAgent) ? (
                <FormRow>
                    <ValidationErrors name={UPDATE_ITEMS_FIELD_NAME}>
                        <RouterCheckbox
                            labelLeft={language.get(AgentDetails.AgentDetails_UpdateExistingItems)}
                            name={UPDATE_ITEMS_FIELD_NAME}
                            disabled={viewOnly}
                        />
                    </ValidationErrors>
                </FormRow>
            ) : (
                <></>
            )}
        </div>
    )
}
